import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { ProductCategory } from "../../../types/product.type";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import FilterField from "../shared/FilterField";
import { ALL_FILTER_VALUE, STATUS_FILTER_OPTIONS } from "../shared/constants";
import {
  WEB_IT_EXPORT_FILTER_OPTIONS,
  WEB_IT_EXPORT_LABEL,
} from "./webItExport";

interface ProductFilterProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  brand: string;
  setBrand: Dispatch<SetStateAction<string>>;
  categoryCode: string;
  setCategoryCode: Dispatch<SetStateAction<string>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  allowExport: string;
  setAllowExport: Dispatch<SetStateAction<string>>;
  categories: ProductCategory[];
}

type FilterChangeEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

// The search box matches name, code and barcode, so it carries the longest input
// of the bar and takes twice the width of the four narrow filters.
const SEARCH_SIZE: GridBaseProps["size"] = { xs: 12, lg: 4 };
const NARROW_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 2 };

export default function ProductFilter({
  search,
  setSearch,
  brand,
  setBrand,
  categoryCode,
  setCategoryCode,
  status,
  setStatus,
  allowExport,
  setAllowExport,
  categories,
}: ProductFilterProps) {
  const handleChangeSearch = (event: FilterChangeEvent) => {
    setSearch(event.target.value);
  };

  const handleChangeBrand = (event: FilterChangeEvent) => {
    setBrand(event.target.value);
  };

  const handleChangeCategory = (event: FilterChangeEvent) => {
    setCategoryCode(event.target.value);
  };

  const handleChangeStatus = (event: FilterChangeEvent) => {
    setStatus(event.target.value);
  };

  const handleChangeAllowExport = (event: FilterChangeEvent) => {
    setAllowExport(event.target.value);
  };

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="ค้นหา" size={SEARCH_SIZE}>
        <TextField
          size="small"
          fullWidth
          placeholder="ชื่อสินค้า / รหัสสินค้า / บาร์โค้ด"
          value={search}
          onChange={handleChangeSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </FilterField>
      <FilterField label="แบรนด์" size={NARROW_SIZE}>
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกชื่อแบรนด์"
          value={brand}
          onChange={handleChangeBrand}
        />
      </FilterField>
      <FilterField label="หมวดสินค้า" size={NARROW_SIZE}>
        <TextField
          select
          size="small"
          fullWidth
          value={categoryCode}
          onChange={handleChangeCategory}
        >
          <MenuItem value={ALL_FILTER_VALUE}>ทั้งหมด</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.uuid} value={category.code}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สถานะ" size={NARROW_SIZE}>
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
        >
          {STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label={WEB_IT_EXPORT_LABEL} size={NARROW_SIZE}>
        <TextField
          select
          size="small"
          fullWidth
          value={allowExport}
          onChange={handleChangeAllowExport}
        >
          {WEB_IT_EXPORT_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
